import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { GroupType } from '../database/schema.js';
import { zql } from '../schema.js';
import { changedItems, itemsDetail, statusItems, valueItem } from '../util/event-log-items.js';
import { titleCase } from '../util/index.js';
import { assertCan, EventLogs } from '../utils.js';
import { DeleteResourceSchema, UpdateResourceStatusSchema, addEventLog } from './util.js';
export const AddUserGroupSchema = z.object({
    name: z.string().trim().nonempty(),
    description: z.string().trim().nonempty(),
    type: GroupType.nonoptional(),
    users: z.array(z.uuidv7()),
});
export const UpdateUserGroupSchema = AddUserGroupSchema.extend({
    id: z.uuidv7(),
});
async function findMembersByIds(tx, memberIds) {
    return await tx.run(zql.userGroups.where('id', 'IN', memberIds).related('user').related('group'));
}
async function findUsersByIds(tx, userIds) {
    return await tx.run(zql.user.where('id', 'IN', userIds));
}
async function createUsersGroupsRelations(tx, payload) {
    const addedUsers = await findUsersByIds(tx, payload.users);
    const group = await findById(tx, payload.groupId);
    const relationsToInsert = addedUsers.flatMap(user => {
        const newUserGroup = {
            id: uuidv7(),
            groupId: payload.groupId,
            userId: user.id,
            ...payload.userActions,
        };
        const promises = [tx.mutate.userGroups.insert(newUserGroup)];
        if (tx.location === 'server') {
            const performedBy = payload.userActions.createdBy || payload.userActions.updatedBy;
            const performedAt = payload.userActions.createdAt || payload.userActions.updatedAt;
            if (performedAt && performedBy) {
                const patches = createPatch({}, newUserGroup);
                promises.push(addEventLog(tx, {
                    id: uuidv7(),
                    resource: 'user-group',
                    resourceId: newUserGroup.id,
                    action: 'create',
                    domain: 'configuration',
                    patches: patches,
                    event: EventLogs.USER_GROUP_ADDED,
                    performedBy,
                    performedAt,
                    details: {
                        userId: user.id,
                        groupId: group?.id,
                        groupName: group?.name,
                        userFullName: user.username,
                    },
                }));
            }
        }
        return promises;
    });
    await Promise.all(relationsToInsert);
}
async function deleteUsersGroupsRelations(tx, payload) {
    const removedMembers = await findMembersByIds(tx, payload.users);
    const relationsToRemove = removedMembers.flatMap(member => {
        // the membership is a link, not a record: delete it, or the many-to-many relation keeps showing
        // the group on the user and the user form saves it back (CY-383). The event log keeps the history.
        const removedRelation = {
            id: member.id,
            deletedAt: payload.userActions.deletedAt,
            deletedBy: payload.userActions.deletedBy,
        };
        const promises = [tx.mutate.userGroups.delete({ id: member.id })];
        if (tx.location === 'server') {
            const patches = createPatch(member, removedRelation);
            promises.push(addEventLog(tx, {
                id: uuidv7(),
                resource: 'user-group',
                resourceId: removedRelation.id,
                action: 'delete',
                domain: 'configuration',
                patches: patches,
                event: EventLogs.USER_GROUP_REMOVED,
                performedBy: removedRelation.deletedBy,
                performedAt: removedRelation.deletedAt,
                details: {
                    userId: member.userId,
                    groupId: member.groupId,
                    groupName: member?.group?.name,
                    userFullName: member?.user?.username,
                },
            }));
        }
        return promises;
    });
    await Promise.all(relationsToRemove);
}
async function findById(tx, groupId) {
    return await tx.run(zql.groups
        .where('id', '=', groupId)
        .related('members', q => q.where('deletedAt', 'IS', null))
        .one());
}
export const userGroups = {
    add: defineMutator(AddUserGroupSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'users.manage');
        const now = Date.now();
        const userActions = {
            createdAt: now,
            createdBy: ctx.user.id,
        };
        const newGroup = {
            id: uuidv7(),
            description: args.description,
            name: titleCase(args.name),
            type: args.type,
            ...userActions,
        };
        await tx.mutate.groups.insert(newGroup);
        if (tx.location === 'server') {
            const patches = createPatch({}, newGroup);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'group',
                resourceId: newGroup.id,
                action: 'create',
                domain: 'configuration',
                event: EventLogs.GROUP_CREATED,
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                details: {
                    id: newGroup.id,
                    name: newGroup.name,
                    ...itemsDetail(valueItem('type', newGroup.type, { valueKey: '{value}' })),
                },
            }, ctx);
        }
        await createUsersGroupsRelations(tx, { users: args.users, groupId: newGroup.id, userActions });
    }),
    update: defineMutator(UpdateUserGroupSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'users.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not Found');
        const now = Date.now();
        const userActions = {
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        const updatedGroup = {
            id: args.id,
            name: titleCase(args.name),
            description: args.description,
            type: args.type,
            ...userActions,
        };
        await tx.mutate.groups.update(updatedGroup);
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedGroup);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'group',
                resourceId: args.id,
                domain: 'configuration',
                event: EventLogs.GROUP_UPDATED,
                action: 'update',
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                details: {
                    id: updatedGroup.id,
                    name: updatedGroup.name,
                    ...itemsDetail(changedItems(prev, updatedGroup, [{ field: 'type', valueKey: '{value}' }])),
                },
            }, ctx);
        }
        const currentMembersIds = new Set(prev.members.map(link => link.userId));
        const newMembersIds = new Set(args.users);
        const addedMembers = args.users?.filter(u => !currentMembersIds.has(u)) || [];
        const deletedMembers = prev.members?.filter(u => !newMembersIds.has(u.userId)) || [];
        if (addedMembers.length) {
            await createUsersGroupsRelations(tx, {
                users: addedMembers,
                groupId: args.id,
                userActions,
            });
        }
        if (deletedMembers.length) {
            await deleteUsersGroupsRelations(tx, {
                users: deletedMembers.map(link => link.id),
                userActions: { deletedAt: now, deletedBy: ctx.user.id },
            });
        }
    }),
    updateStatus: defineMutator(UpdateResourceStatusSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'users.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not found');
        const updatedGroup = {
            id: args.id,
            status: args.status,
            updatedAt: Date.now(),
            updatedBy: ctx.user.id,
        };
        await tx.mutate.groups.update(updatedGroup);
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedGroup);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'group',
                resourceId: args.id,
                domain: 'configuration',
                event: EventLogs.GROUP_UPDATED_STATUS,
                action: 'update',
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: Date.now(),
                status: 'OK',
                details: {
                    id: prev.id,
                    name: prev.name,
                    // groups store no status, so there is no old value to show
                    ...itemsDetail(statusItems(null, args.status)),
                },
            }, ctx);
        }
    }),
    delete: defineMutator(DeleteResourceSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'users.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const userActions = {
            deletedAt: now,
            deletedBy: ctx.user.id,
        };
        const updatedGroup = {
            id: args.id,
            name: `${prev.name}_deleted_${now}`,
            ...userActions,
        };
        await tx.mutate.groups.update(updatedGroup);
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedGroup);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'group',
                resourceId: args.id,
                action: 'delete',
                domain: 'configuration',
                event: EventLogs.GROUP_DELETED,
                patches: patches,
                performedBy: updatedGroup.deletedBy,
                performedAt: updatedGroup.deletedAt,
                status: 'WARNING',
                details: {
                    id: prev.id,
                    name: prev.name,
                },
            }, ctx);
        }
        await deleteUsersGroupsRelations(tx, {
            users: prev.members.map(link => link.id),
            userActions,
        });
    }),
};
//# sourceMappingURL=user-group.mutators.js.map